/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, LogOut, BarChart3, FileDown, Beef, Stethoscope, Heart, Wheat, Coins,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [exporting, setExporting] = useState(false);

  useTrackModuleView("analytics");

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") {
      axios.get(`${API}/analytics/overview`, { withCredentials: true })
        .then((r) => setData(r.data))
        .catch((e) => toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) }))
        .finally(() => setLoadingData(false));
    }
  }, [user]); // eslint-disable-line

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/analytics/export.pdf`, { withCredentials: true, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "mazra3ti-rapport.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t.app.health.pdf_generated);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail || "—") });
    } finally { setExporting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button onClick={logout} variant="ghost" size="sm" className="text-white/80">
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <BarChart3 size={14} /> {t.app.analytics.overline}
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.analytics.title}</h1>
            <p className="mt-1 text-[#5C6B5C]">{t.app.analytics.subtitle}</p>
          </div>
          <Button onClick={exportPdf} disabled={exporting || !data} data-testid="export-analytics-pdf" className="h-12 px-5 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
            <FileDown size={18} className="me-2" />
            {exporting ? t.app.analytics.exporting : t.app.analytics.export_pdf}
          </Button>
        </div>

        {loadingData || !data ? (
          <div className="p-20 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
        ) : (
          <>
            <Section icon={Beef} title={t.app.analytics.section_herd}>
              <KpiCard label={t.app.analytics.animals_total} value={data.animals.total} />
              <KpiCard label={t.app.analytics.animals_active} value={data.animals.active} accent="#1C7A3C" />
              <KpiCard label={t.app.analytics.animals_sold} value={data.animals.sold} accent="#1E6BBA" />
              <KpiCard label={t.app.analytics.animals_dead} value={data.animals.dead} accent="#8A8A8A" />
            </Section>

            <Section icon={Stethoscope} title={t.app.analytics.section_health}>
              <KpiCard label={t.app.analytics.health_total} value={data.health.total_records} />
              <KpiCard label={t.app.analytics.health_cost} value={`${data.health.total_cost_tnd.toFixed(0)} TND`} accent="#C84B31" />
            </Section>

            <Section icon={Heart} title={t.app.analytics.section_repro}>
              <KpiCard label={t.app.analytics.repro_events} value={data.reproduction.total_events} />
              <KpiCard label={t.app.analytics.repro_active_preg} value={data.reproduction.active_pregnancies} accent="#DB2777" />
              <KpiCard label={t.app.analytics.repro_births} value={data.reproduction.total_births} accent="#1C7A3C" />
              <KpiCard label={t.app.analytics.repro_offspring} value={data.reproduction.offspring_alive} accent="#E05A3D" />
            </Section>

            <Section icon={Wheat} title={t.app.analytics.section_nutrition}>
              <KpiCard label={t.app.analytics.nutri_stocks} value={data.nutrition.stock_items} />
              <KpiCard label={t.app.analytics.nutri_value} value={`${data.nutrition.stock_value_tnd.toFixed(0)} TND`} accent="#1E6BBA" />
              <KpiCard label={t.app.analytics.nutri_feedings} value={data.nutrition.total_feedings} />
              <KpiCard label={t.app.analytics.nutri_cost} value={`${data.nutrition.feed_cost_tnd.toFixed(0)} TND`} accent="#C84B31" />
            </Section>

            <Section icon={Coins} title={t.app.analytics.section_costs}>
              <KpiCard label={t.app.analytics.cost_total} value={`${data.costs.total_tnd.toFixed(0)} TND`} accent="#1C3A27" size="lg" />
              <KpiCard label={t.app.analytics.cost_per_animal} value={`${data.costs.avg_per_active_animal_tnd.toFixed(2)} TND`} accent="#E05A3D" size="lg" />
            </Section>
          </>
        )}
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="flex items-center gap-2 font-display text-xl font-bold text-[#1C3A27] mb-4">
        <Icon size={20} className="text-[#E05A3D]" /> {title}
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
    </section>
  );
}

function KpiCard({ label, value, accent = "#1C3A27", size = "md" }) {
  const valueCls = size === "lg" ? "text-4xl md:text-5xl" : "text-3xl";
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-5">
      <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold truncate">{label}</div>
      <div className={`font-display font-black mt-1 ${valueCls}`} style={{ color: accent }}>{value}</div>
    </div>
  );
}
